import { callObsidianAI } from '../utils/obsidian';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MdShield, MdWarning, MdCheckCircle, MdError, MdLock, MdMusicNote, MdVisibility, MdRecordVoiceOver, MdDescription } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getVideos, getChannels } from '../utils/api';

const platformPolicies = {
  youtube: {
    name: 'YouTube',
    icon: <FaYoutube style={{ color: '#ff0000', fontSize: '20px' }} />,
    color: '#ff0000',
    bg: 'rgba(255,0,0,0.08)',
    border: 'rgba(255,0,0,0.2)',
    policies: [
      // Hate Speech
      'No content promoting hatred based on race, ethnicity, nationality, religion, gender, sexual orientation, age or disability',
      'No dehumanising language or imagery targeting protected groups',
      'No content that glorifies or promotes hate groups or supremacist ideologies',
      // Violence
      'No graphic violence, gore or gruesome content',
      'No content that incites others to commit violent acts',
      'No footage of violent crimes or accidents for shock value',
      'No animal abuse or cruelty content',
      // Adult Content
      'No nudity or sexually explicit content',
      'No content that sexually exploits minors in any form',
      'No content that promotes sexual services',
      // Harassment
      'No targeted harassment or cyberbullying of individuals',
      'No content designed to make someone fear for their safety',
      'No revealing private information without consent (doxxing)',
      // Harmful Content
      'No instructions or tutorials for dangerous activities',
      'No content that promotes or glorifies self harm or suicide',
      'No content promoting eating disorders',
      'No content showing or promoting drug use',
      // Misinformation
      'No medically inaccurate content that could cause real harm',
      'No election misinformation or voter suppression content',
      'No content denying well documented violent events',
      'No manipulated media presented as genuine',
      // Spam and Misleading
      'No misleading titles, thumbnails or descriptions that deceive viewers',
      'No artificially inflating engagement metrics',
      'No content that impersonates another creator or channel',
      'No repetitive content uploaded with the intent to spam',
      // Copyright
      'No use of copyrighted music without proper license or permission',
      'No reproduction of copyrighted films, TV shows or broadcasts',
      'No use of copyrighted video game content beyond fair use',
      'Thumbnail must accurately represent video content',
      // Terrorism
      'No content promoting terrorist organisations or violent extremism',
      'No recruitment material for extremist groups',
      // Child Safety
      'No content that endangers minors physically or emotionally',
      'No content that sexualises minors in any way',
      'No grooming or exploitative content involving children',
      // Regulated Goods
      'No promotion of sale of firearms or illegal weapons',
      'No promotion of drugs or illegal substances',
      'No promotion of counterfeit goods',
    ]
  },
  facebook: {
    name: 'Facebook',
    icon: <FaFacebook style={{ color: '#1877f2', fontSize: '20px' }} />,
    color: '#1877f2',
    bg: 'rgba(24,119,242,0.08)',
    border: 'rgba(24,119,242,0.2)',
    policies: [
      // Hate Speech
      'No content attacking people based on protected characteristics including race, religion, gender, sexual orientation or disability',
      'No content that dehumanises people using harmful stereotypes',
      'No content that calls for exclusion or segregation of protected groups',
      'No hate group symbolism, imagery or propaganda',
      // Violence
      'No graphic violence or content shared for sadistic pleasure',
      'No content that facilitates or celebrates acts of violence',
      'No credible threats of violence against individuals or groups',
      'No content glorifying mass shootings or terrorist attacks',
      // Adult Content
      'No nudity or sexual activity',
      'No sexual solicitation or offers of sexual services',
      'No content that sexually exploits adults or minors',
      // Bullying and Harassment
      'No targeted attacks on private individuals',
      'No content designed to degrade or shame individuals',
      'No repeated unwanted contact or following behaviour',
      'No exposing private information about others',
      // Child Safety
      'No sexual content involving minors',
      'No content that facilitates exploitation of children',
      'No content that puts children at risk',
      // Misinformation
      'No health misinformation that contradicts expert consensus',
      'No climate change denial framed as scientific fact',
      'No election misinformation about voting processes',
      'No fabricated quotes attributed to real people',
      // Privacy
      'No sharing of private information without consent',
      'No content that violates others privacy',
      // Intellectual Property
      'No posting content you do not have rights to share',
      'No reproducing copyrighted music, video or images',
      // Spam
      'No engagement bait posts asking for likes, shares or comments',
      'No coordinated inauthentic behaviour or fake accounts',
      'No misleading content designed to drive traffic',
      // Regulated Goods
      'No selling or promoting firearms, drugs or illegal items',
      'No promotion of gambling without proper disclosures',
      'No promotion of tobacco or vaping products',
      // Self Harm
      'No content promoting or glorifying suicide or self injury',
      'No content that provides methods or instructions for self harm',
    ]
  },
  tiktok: {
    name: 'TikTok',
    icon: <FaTiktok style={{ color: '#e0e0e0', fontSize: '20px' }} />,
    color: '#e0e0e0',
    bg: 'rgba(255,255,255,0.04)',
    border: 'rgba(255,255,255,0.1)',
    policies: [
      // Violent Extremism
      'No content promoting, glorifying or supporting terrorist organisations',
      'No recruitment material for violent extremist groups',
      'No content celebrating mass violence or atrocities',
      // Hateful Behaviour
      'No content that attacks or demeans people based on protected attributes',
      'No content that promotes supremacist ideologies',
      'No dehumanising language targeting any group',
      // Dangerous Acts
      'No content depicting dangerous activities that could be imitated',
      'No content promoting or encouraging dangerous challenges',
      'No content showing illegal activities like drug use or weapons handling',
      // Minor Safety
      'No sexual content involving minors',
      'No grooming behaviour or content targeting children',
      'No content that puts minors at risk of exploitation',
      // Adult Content
      'No nudity or sexual content',
      'No sexually suggestive content involving minors',
      // Harassment
      'No targeted harassment of individuals',
      'No content designed to intimidate or threaten',
      'No coordinated harassment campaigns',
      // Privacy
      'No sharing private information without consent',
      'No recording or posting others without their knowledge in private settings',
      // Misinformation
      'No medical misinformation that could cause harm',
      'No election misinformation',
      'No conspiracy theories presented as fact',
      'No manipulated media without clear disclosure',
      // Copyright
      'No use of copyrighted music without proper license',
      'No reproducing copyrighted content without permission',
      'No content that violates intellectual property rights',
      // Spam
      'No artificial inflation of metrics or engagement',
      'No spam or repetitive content',
      'No impersonation of other accounts or creators',
      // Self Harm
      'No content promoting suicide or self injury',
      'No content depicting graphic self harm',
      'No eating disorder promotion or glorification',
      // Regulated Goods
      'No promotion of tobacco, alcohol or drugs to minors',
      'No promotion of illegal weapons or firearms',
      'No promotion of counterfeit goods',
    ]
  },
  instagram: {
    name: 'Instagram',
    icon: <FaInstagram style={{ color: '#e1306c', fontSize: '20px' }} />,
    color: '#e1306c',
    bg: 'rgba(225,48,108,0.08)',
    border: 'rgba(225,48,108,0.2)',
    policies: [
      // Adult Content
      'No nudity or sexual acts including implied sexual activity',
      'No sexually explicit content in any format',
      'No content that sexually exploits adults or minors',
      // Child Safety
      'No sexual content involving minors',
      'No content that endangers children',
      'No content that facilitates grooming or exploitation of minors',
      // Hate Speech
      'No content that attacks people based on protected characteristics',
      'No hate group imagery or propaganda',
      'No content that degrades or dehumanises people',
      // Violence
      'No graphic violence or gore',
      'No content that incites or promotes violence',
      'No threats of violence against individuals or groups',
      // Harassment
      'No bullying or harassment of individuals',
      'No repeated unwanted contact',
      'No content designed to shame or humiliate others',
      // Self Harm
      'No content promoting suicide or self injury',
      'No eating disorder promotion including thinspo or meanspo content',
      'No content providing methods for self harm',
      // Privacy
      'No sharing of others private information',
      'No posting images of people without consent',
      // Intellectual Property
      'No posting content you do not own or have rights to',
      'No reproducing copyrighted music or video content',
      'No selling counterfeit goods',
      // Regulated Goods
      'No selling or promoting firearms or illegal weapons',
      'No promotion of drugs or controlled substances',
      'No promotion of tobacco or vaping products to minors',
      // Misinformation
      'No health misinformation',
      'No election or voting misinformation',
      'No fabricated news or manipulated media',
      // Spam
      'No fake accounts or impersonation',
      'No coordinated inauthentic behaviour',
      'No buying or selling followers or engagement',
      'No repetitive comments or messages',
    ]
  }
};

const ScanLayer = ({ title, icon, status, description, isLocked, apiKeyName }) => {
  const getStatusColor = () => {
    if (isLocked) return 'var(--text-light)';
    if (status === 'pass') return '#00c853';
    if (status === 'fail') return '#ef4444';
    if (status === 'warning') return '#f59e0b';
    return 'var(--text-secondary)';
  };

  const getStatusIcon = () => {
    if (isLocked) return <MdLock />;
    if (status === 'pass') return <MdCheckCircle />;
    if (status === 'fail') return <MdError />;
    if (status === 'warning') return <MdWarning />;
    return null;
  };

  return (
    <div style={{
      padding: '16px', borderRadius: '12px',
      background: isLocked ? 'var(--bg-dark)' : status === 'pass'
        ? 'rgba(0,200,83,0.08)' : status === 'fail'
          ? 'rgba(239,68,68,0.08)' : status === 'warning'
            ? 'rgba(245,158,11,0.08)' : 'var(--bg-dark)',
      border: `1px solid ${isLocked ? 'var(--border)' : status === 'pass'
        ? 'rgba(0,200,83,0.3)' : status === 'fail'
          ? 'rgba(239,68,68,0.3)' : status === 'warning'
            ? 'rgba(245,158,11,0.3)' : 'var(--border)'}`,
      marginBottom: '10px',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: description ? '8px' : '0' }}>
        <span style={{ fontSize: '20px', color: getStatusColor() }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>{title}</span>
          {isLocked && (
            <span style={{
              marginLeft: '8px', fontSize: '11px', color: 'var(--text-light)',
              background: 'var(--bg-card)', padding: '2px 8px', borderRadius: '10px',
              border: '1px solid var(--border)'
            }}>
              Connect {apiKeyName} to activate
            </span>
          )}
        </div>
        <span style={{ fontSize: '20px', color: getStatusColor() }}>{getStatusIcon()}</span>
      </div>
      {description && (
        <p style={{
          fontSize: '13px', color: isLocked ? 'var(--text-light)' : 'var(--text-secondary)',
          lineHeight: '1.6', marginLeft: '32px', whiteSpace: 'pre-wrap'
        }}>
          {description}
        </p>
      )}
    </div>
  );
};

const ViolationItem = ({ issue, severity }) => (
  <div style={{
    display: 'flex', gap: '10px', padding: '12px 14px',
    background: severity === 'high' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
    borderRadius: '10px', marginBottom: '8px',
    border: `1px solid ${severity === 'high' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`
  }}>
    <span style={{ color: severity === 'high' ? '#ef4444' : '#f59e0b', fontSize: '18px', flexShrink: 0 }}>
      {severity === 'high' ? <MdError /> : <MdWarning />}
    </span>
    <div>
      <span style={{
        fontSize: '11px', fontWeight: '700',
        color: severity === 'high' ? '#ef4444' : '#f59e0b',
        textTransform: 'uppercase', letterSpacing: '0.05em'
      }}>
        {severity === 'high' ? 'High Risk' : 'Warning'}
      </span>
      <p style={{ fontSize: '13px', color: 'var(--text-primary)', marginTop: '4px', lineHeight: '1.6' }}>
        {issue}
      </p>
    </div>
  </div>
);

const GuardianAI = () => {
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['youtube']);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [videosRes, channelsRes] = await Promise.all([getVideos(), getChannels()]);
      setVideos(videosRes.data.data || []);
      setChannels(channelsRes.data.data || []);
      setDataLoaded(true);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const getChannelInfo = (channelId) => channels.find(c => c.id === channelId);

  const runGuardianScan = async () => {
    if (!selectedVideo) {
      toast.error('Please select a video to scan');
      return;
    }
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform to scan against');
      return;
    }

    setScanning(true);
    setScanResults(null);

    const channel = getChannelInfo(selectedVideo.channel_id);

    const platformPoliciesText = selectedPlatforms.map(p => {
      const policy = platformPolicies[p];
      return `${policy.name} COMPLETE POLICY DATABASE (${policy.policies.length} rules):\n${policy.policies.map((rule, i) => `${i + 1}. ${rule}`).join('\n')}`;
    }).join('\n\n');;

    const prompt = `You are GuardianAI, an expert content policy analyst for social media platforms. Your job is to thoroughly analyse video metadata and detect ANY potential policy violations before upload.

Be EXTREMELY thorough. Do not miss anything. Check every word, phrase, implication and context.
You have access to a comprehensive policy database. Cross reference every element of the metadata against every single rule.
Pay special attention to:
- Double meaning words or phrases that could be interpreted as violating policies
- Keywords that trigger algorithmic flags even if not intentionally harmful
- Metadata that could be fine on one platform but violate another
- Subtle copyright risks in titles or descriptions
- Any language that could be considered clickbait or misleading
- Words associated with restricted categories even in neutral contexts
This scan is the last line of defence before upload. Be thorough as if a human policy reviewer is checking your work.

VIDEO METADATA TO ANALYSE:
- Title: ${selectedVideo.youtube_title || selectedVideo.title || 'Not provided'}
- YouTube Description: ${selectedVideo.youtube_description || 'Not provided'}
- Facebook Title: ${selectedVideo.facebook_title || 'Not provided'}
- Facebook Description: ${selectedVideo.facebook_description || 'Not provided'}
- TikTok Caption: ${selectedVideo.tiktok_title || 'Not provided'}
- Instagram Caption: ${selectedVideo.instagram_title || 'Not provided'}
- Tags: ${selectedVideo.tags || 'Not provided'}
- Category: ${selectedVideo.category || 'Not provided'}
- Visibility: ${selectedVideo.visibility || 'public'}
- Channel/Platform: ${channel?.platform || 'Unknown'}
- Thumbnail Available: ${selectedVideo.thumbnail_path ? 'Yes' : 'No'}

PLATFORMS TO CHECK AGAINST:
${selectedPlatforms.join(', ')}

PLATFORM POLICIES:
${platformPoliciesText}

Respond ONLY in valid JSON with no other text:
{
  "overall_safety": "safe|warning|danger",
  "overall_message": "Brief overall assessment",
  "metadata_scan": {
    "status": "pass|warning|fail",
    "issues": [
      {
        "field": "which field has the issue",
        "issue": "detailed description of the violation or risk",
        "severity": "high|medium",
        "suggestion": "specific fix recommendation"
      }
    ],
    "summary": "overall metadata scan summary"
  },
  "platform_results": {
    "youtube": {
      "status": "pass|warning|fail",
      "violations": ["specific violation 1", "specific violation 2"],
      "warnings": ["warning 1", "warning 2"],
      "safe_elements": ["what is safe 1", "what is safe 2"],
      "recommendation": "specific recommendation for this platform"
    },
    "facebook": {
      "status": "pass|warning|fail",
      "violations": [],
      "warnings": [],
      "safe_elements": [],
      "recommendation": ""
    },
    "tiktok": {
      "status": "pass|warning|fail",
      "violations": [],
      "warnings": [],
      "safe_elements": [],
      "recommendation": ""
    },
    "instagram": {
      "status": "pass|warning|fail",
      "violations": [],
      "warnings": [],
      "safe_elements": [],
      "recommendation": ""
    }
  },
  "copyright_risks": ["any copyright risks detected in metadata"],
  "immediate_fixes": ["fix 1 to apply before uploading", "fix 2", "fix 3"],
  "safe_to_upload": true
}

Only include platform results for these platforms: ${selectedPlatforms.join(', ')}`;

    try {
      const data = await callObsidianAI(
        [{ role: 'user', content: prompt }],
        '',
        3000
      );
      const cleanResponse = data.content[0].text.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(cleanResponse);
      setScanResults(parsed);
      toast.success('Guardian scan complete!');

    } catch (error) {
      toast.error('Scan failed: ' + error.message);
    } finally {
      setScanning(false);
    }
  };

  const getOverallColor = (status) => {
    if (status === 'safe') return '#00c853';
    if (status === 'warning') return '#f59e0b';
    return '#ef4444';
  };

  const getStatusBadge = (status) => {
    if (status === 'pass') return <span className="badge badge-success">Pass</span>;
    if (status === 'warning') return <span className="badge badge-warning">Warning</span>;
    if (status === 'fail') return <span className="badge badge-danger">Fail</span>;
    return null;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><span className="gradient-text">GuardianAI</span> 🛡</h1>
        <p>Deep content policy scanning across all platforms before you upload — zero violations guaranteed</p>
      </div>

      <div className="grid-2">
        <div>
          {/* Video Selection */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Select Video to Scan</span>
            </div>

            {!dataLoaded ? (
              <button className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={loadData} disabled={loading}>
                {loading
                  ? <><div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> Loading...</>
                  : 'Load My Videos'}
              </button>
            ) : (
              <>
                <select className="form-select" style={{ marginBottom: '16px' }}
                  onChange={(e) => {
                    const video = videos.find(v => v.id === parseInt(e.target.value));
                    setSelectedVideo(video || null);
                    setScanResults(null);
                  }}>
                  <option value="">Select a video...</option>
                  {videos.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.youtube_title || v.title || `Video #${v.id}`}
                    </option>
                  ))}
                </select>

                {selectedVideo && (
                  <div style={{
                    background: 'var(--bg-dark)', borderRadius: '10px',
                    padding: '14px', marginBottom: '16px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '6px', color: 'var(--text-primary)' }}>
                      {selectedVideo.youtube_title || selectedVideo.title || 'Untitled'}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {selectedVideo.youtube_title && <span className="badge badge-youtube">Title</span>}
                      {selectedVideo.youtube_description && <span className="badge badge-info">Description</span>}
                      {selectedVideo.tags && <span className="badge badge-secondary">Tags</span>}
                      {selectedVideo.thumbnail_path
                        ? <span className="badge badge-success">Thumbnail</span>
                        : <span className="badge badge-danger">No Thumbnail</span>}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Platform Selection */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Select Platforms to Check Against</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {Object.entries(platformPolicies).map(([platform, config]) => (
                <div
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
                    background: selectedPlatforms.includes(platform) ? config.bg : 'var(--bg-dark)',
                    border: `1px solid ${selectedPlatforms.includes(platform) ? config.border : 'var(--border)'}`,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {config.icon}
                  <span style={{ fontWeight: '500', color: 'var(--text-primary)', flex: 1 }}>
                    {config.name}
                  </span>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%',
                    border: `2px solid ${selectedPlatforms.includes(platform) ? config.color : 'var(--border)'}`,
                    background: selectedPlatforms.includes(platform) ? config.color : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}>
                    {selectedPlatforms.includes(platform) && (
                      <span style={{ color: 'white', fontSize: '11px', fontWeight: '700' }}>✓</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scan Layers */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Scan Layers</span>
            </div>
            <ScanLayer
              title="Layer 1 — Metadata Policy Scan"
              icon={<MdDescription />}
              status={scanResults ? scanResults.metadata_scan?.status : null}
              description={scanResults ? scanResults.metadata_scan?.summary : 'Checks title, description, tags and captions against platform policies'}
              isLocked={false}
            />
            <ScanLayer
              title="Layer 2 — Audio & Speech Scan"
              icon={<MdRecordVoiceOver />}
              status={null}
              description="Transcribes and analyses spoken content for policy violations"
              isLocked={true}
              apiKeyName="AssemblyAI"
            />
            <ScanLayer
              title="Layer 3 — Visual Frame Scan"
              icon={<MdVisibility />}
              status={null}
              description="Analyses video frames for visual policy violations"
              isLocked={true}
              apiKeyName="Google Vision"
            />
            <ScanLayer
              title="Layer 4 — Music Copyright Scan"
              icon={<MdMusicNote />}
              status={null}
              description="Detects copyrighted music in your video"
              isLocked={true}
              apiKeyName="AudD"
            />
          </div>

          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: '16px' }}
            onClick={runGuardianScan}
            disabled={!selectedVideo || scanning || selectedPlatforms.length === 0}
          >
            {scanning ? (
              <><div className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px' }} /> Scanning...</>
            ) : (
              <><MdShield /> Run Guardian Scan</>
            )}
          </button>
        </div>

        <div>
          {scanResults ? (
            <>
              {/* Overall Result */}
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '20px',
                  padding: '20px', borderRadius: '14px',
                  background: scanResults.overall_safety === 'safe'
                    ? 'rgba(0,200,83,0.1)' : scanResults.overall_safety === 'warning'
                      ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                  border: `1px solid ${scanResults.overall_safety === 'safe'
                    ? 'rgba(0,200,83,0.3)' : scanResults.overall_safety === 'warning'
                      ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)'}`
                }}>
                  <div style={{
                    width: '64px', height: '64px', borderRadius: '50%',
                    background: getOverallColor(scanResults.overall_safety),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '28px', flexShrink: 0,
                    boxShadow: `0 0 20px ${getOverallColor(scanResults.overall_safety)}40`
                  }}>
                    {scanResults.overall_safety === 'safe' ? '✓' : scanResults.overall_safety === 'warning' ? '⚠' : '✕'}
                  </div>
                  <div>
                    <div style={{
                      fontSize: '22px', fontWeight: '800',
                      color: getOverallColor(scanResults.overall_safety),
                      textTransform: 'capitalize'
                    }}>
                      {scanResults.overall_safety === 'safe' ? 'Safe to Upload' : scanResults.overall_safety === 'warning' ? 'Proceed with Caution' : 'Do Not Upload'}
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      {scanResults.overall_message}
                    </p>
                  </div>
                </div>
              </div>

              {/* Immediate Fixes */}
              {scanResults.immediate_fixes && scanResults.immediate_fixes.length > 0 && (
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div className="card-header">
                    <span className="card-title">Immediate Fixes Required</span>
                    <span className="badge badge-danger">{scanResults.immediate_fixes.length} fixes</span>
                  </div>
                  {scanResults.immediate_fixes.map((fix, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: '10px', padding: '12px 14px',
                      background: 'rgba(239,68,68,0.08)', borderRadius: '10px',
                      marginBottom: '8px', border: '1px solid rgba(239,68,68,0.2)'
                    }}>
                      <span style={{ color: '#ef4444', fontWeight: '700' }}>{i + 1}</span>
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.6' }}>{fix}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Metadata Issues */}
              {scanResults.metadata_scan?.issues && scanResults.metadata_scan.issues.length > 0 && (
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div className="card-header">
                    <span className="card-title">Metadata Issues Found</span>
                    {getStatusBadge(scanResults.metadata_scan.status)}
                  </div>
                  {scanResults.metadata_scan.issues.map((issue, i) => (
                    <div key={i} style={{
                      padding: '14px', background: 'var(--bg-dark)',
                      borderRadius: '10px', marginBottom: '10px',
                      border: `1px solid ${issue.severity === 'high' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>
                          {issue.field}
                        </span>
                        <span className={`badge badge-${issue.severity === 'high' ? 'danger' : 'warning'}`}>
                          {issue.severity}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: '1.6' }}>
                        {issue.issue}
                      </p>
                      <div style={{
                        background: 'rgba(0,200,83,0.08)', padding: '8px 12px',
                        borderRadius: '8px', border: '1px solid rgba(0,200,83,0.2)'
                      }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary)' }}>FIX: </span>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{issue.suggestion}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Platform Results */}
              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="card-header">
                  <span className="card-title">Platform Results</span>
                </div>
                {Object.entries(scanResults.platform_results || {}).map(([platform, result]) => {
                  const config = platformPolicies[platform];
                  if (!config) return null;
                  return (
                    <div key={platform} style={{
                      background: config.bg, border: `1px solid ${config.border}`,
                      borderRadius: '12px', padding: '16px', marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {config.icon}
                          <span style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>
                            {config.name}
                          </span>
                        </div>
                        {getStatusBadge(result.status)}
                      </div>

                      {result.violations && result.violations.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          {result.violations.map((v, i) => (
                            <ViolationItem key={i} issue={v} severity="high" />
                          ))}
                        </div>
                      )}

                      {result.warnings && result.warnings.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          {result.warnings.map((w, i) => (
                            <ViolationItem key={i} issue={w} severity="medium" />
                          ))}
                        </div>
                      )}

                      {result.safe_elements && result.safe_elements.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          {result.safe_elements.map((s, i) => (
                            <div key={i} style={{
                              display: 'flex', gap: '8px', padding: '8px 12px',
                              background: 'rgba(0,200,83,0.08)', borderRadius: '8px',
                              marginBottom: '6px', border: '1px solid rgba(0,200,83,0.2)'
                            }}>
                              <span style={{ color: '#00c853' }}>✓</span>
                              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{s}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {result.recommendation && (
                        <div style={{
                          background: 'var(--bg-dark)', padding: '10px 14px',
                          borderRadius: '8px', border: '1px solid var(--border)'
                        }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary)' }}>
                            RECOMMENDATION:
                          </span>
                          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', lineHeight: '1.6' }}>
                            {result.recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Copyright Risks */}
              {scanResults.copyright_risks && scanResults.copyright_risks.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">Copyright Risks</span>
                    <span className="badge badge-warning">{scanResults.copyright_risks.length} risks</span>
                  </div>
                  {scanResults.copyright_risks.map((risk, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: '10px', padding: '12px 14px',
                      background: 'rgba(245,158,11,0.08)', borderRadius: '10px',
                      marginBottom: '8px', border: '1px solid rgba(245,158,11,0.2)'
                    }}>
                      <span style={{ color: '#f59e0b' }}>⚠</span>
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.6' }}>{risk}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="card" style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <MdShield style={{ fontSize: '80px', color: 'var(--primary)', opacity: 0.2, marginBottom: '20px' }} />
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '20px' }}>
                  GuardianAI Ready
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '280px', lineHeight: '1.7' }}>
                  Select a video, choose your target platforms and run a full Guardian scan to detect any policy violations before uploading
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuardianAI;